import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
} from '@mui/material';
import {
  Accessible,
  TrendingUp,
  QuestionAnswer,
  Description,
  RateReview,
  ImageSearch,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import firebase from 'firebase/app';
import 'firebase/firestore';
import TabDescription from './TabDescription';
import { useRegionContext } from '../../region/RegionContext';

const FEATURE_COLORS = {
  accessibility_question: '#1976D2',
  ai_access_narrative: '#7B1FA2',
  alt_text_generated: '#00897B',
  access_review: '#E65100',
};

const AccessibilityAnalyticsTab = () => {
  const { db } = useRegionContext();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30days');
  const [accessUsers, setAccessUsers] = useState([]);
  const [aiEvents, setAiEvents] = useState([]);
  const [accessReviewCount, setAccessReviewCount] = useState(0);
  const [altTextStats, setAltTextStats] = useState({ total: 0, withAlt: 0 });
  const [narrativeCount, setNarrativeCount] = useState(0);

  const getStartDate = useCallback(() => {
    const start = new Date();
    if (dateRange === '7days') start.setDate(start.getDate() - 7);
    else if (dateRange === '30days') start.setDate(start.getDate() - 30);
    else if (dateRange === '90days') start.setDate(start.getDate() - 90);
    else if (dateRange === 'year') start.setFullYear(start.getFullYear() - 1);
    return start;
  }, [dateRange]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = getStartDate();

      const [usersSnap, aiSnap, reviewSnap, bizSnap] = await Promise.all([
        // Users with accessibility flag
        db.collection('Users').where('userAccessibility', '==', true).limit(500).get(),

        // AI analytics — accessibility queries
        db.collection('_aiAnalytics')
          .where('timestamp', '>=', firebase.firestore.Timestamp.fromDate(startDate))
          .orderBy('timestamp', 'desc')
          .limit(500)
          .get(),

        // Access reviews count (completed only)
        db.collectionGroup('AccessReviews')
          .where('reviewComplete', '==', true)
          .limit(500)
          .get(),

        // Business docs with aiAccessNarrative (sample for count)
        db.collection('Business')
          .where('aiAccessNarrative', '>', '')
          .limit(500)
          .get(),
      ]);

      const users = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAccessUsers(users);

      const events = aiSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAiEvents(events);

      setAccessReviewCount(reviewSnap.size);
      setNarrativeCount(bizSnap.size);

      // Alt text stats: count businesses with at least one alt text field populated
      const allBizSnap = await db.collection('Business')
        .where('bizVerified', '==', true)
        .limit(500)
        .get();
      let totalWithImages = 0;
      let totalWithAlt = 0;
      allBizSnap.docs.forEach(doc => {
        const d = doc.data();
        if (d.bizPic1 || (Array.isArray(d.bizPic) && d.bizPic.length > 0)) {
          totalWithImages++;
          if (d.bizPic1Alt || d.altTextVerified) totalWithAlt++;
        }
      });
      setAltTextStats({ total: totalWithImages, withAlt: totalWithAlt });
    } catch (err) {
      console.error('Error loading accessibility analytics:', err);
    } finally {
      setLoading(false);
    }
  }, [getStartDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading accessibility analytics...</Typography>
      </Box>
    );
  }

  // --- Metrics ---
  const totalAccessUsers = accessUsers.length;

  // Signup trend by month (aggregate, no PII)
  const signupByMonth = {};
  accessUsers.forEach(u => {
    const ts = u.userCreatedAt?.toDate?.() || u.createdAt?.toDate?.();
    if (!ts) return;
    const key = ts.toLocaleDateString('en-AU', { year: 'numeric', month: 'short' });
    signupByMonth[key] = (signupByMonth[key] || 0) + 1;
  });
  const signupTrend = Object.entries(signupByMonth)
    .map(([date, count]) => ({ date, count }))
    .slice(-12);

  // State distribution (aggregate)
  const stateBreakdown = {};
  accessUsers.forEach(u => {
    const state = u.userState || 'Unknown';
    stateBreakdown[state] = (stateBreakdown[state] || 0) + 1;
  });
  const stateData = Object.entries(stateBreakdown)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);

  // AI accessibility feature engagement
  const accessibilityQueries = aiEvents.filter(e => e.intent === 'accessibility_question');
  const accessDataSourceUsage = aiEvents.filter(e =>
    e.dataSources && e.dataSources.includes('accessibility')
  );

  // Accessibility queries over time
  const queriesByDay = {};
  accessibilityQueries.forEach(e => {
    const ts = e.timestamp?.toDate?.();
    if (!ts) return;
    const key = ts.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
    if (!queriesByDay[key]) queriesByDay[key] = { date: key, queries: 0 };
    queriesByDay[key].queries += 1;
  });
  const queriesOverTime = Object.values(queriesByDay).reverse();

  // Feature engagement breakdown (for pie chart)
  const featureEngagement = [
    { name: 'Accessibility Q&A', value: accessibilityQueries.length },
    { name: 'AI Narratives', value: narrativeCount },
    { name: 'Alt Text Generated', value: altTextStats.withAlt },
    { name: 'Access Reviews', value: accessReviewCount },
  ].filter(e => e.value > 0);

  const featureColors = ['#1976D2', '#7B1FA2', '#00897B', '#E65100'];

  // Unique accessibility users engaging with AI
  const uniqueAccessAiUsers = new Set(
    accessibilityQueries.filter(e => e.uid).map(e => e.uid)
  ).size;

  // Query source breakdown for accessibility queries
  const accessSourceCounts = {};
  accessibilityQueries.forEach(e => {
    if (e.source) {
      const label = e.source === 'home_longpress' ? 'Voice Search' : 'Venue FAB';
      accessSourceCounts[label] = (accessSourceCounts[label] || 0) + 1;
    }
  });
  const accessSourceData = Object.entries(accessSourceCounts)
    .map(([name, value]) => ({ name, value }));

  return (
    <Box>
      <TabDescription>
        Accessibility usage analytics tracking how many users have identified as having an accessibility
        requirement and which AI accessibility features they engage with. All data is presented in aggregate
        form only — no personally identifiable information is displayed, stored, or exported, in compliance
        with the Piatto Privacy Policy.
      </TabDescription>

      {/* Date range filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              label="Date Range"
            >
              <MenuItem value="7days">Last 7 Days</MenuItem>
              <MenuItem value="30days">Last 30 Days</MenuItem>
              <MenuItem value="90days">Last 90 Days</MenuItem>
              <MenuItem value="year">Last Year</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Accessible sx={{ fontSize: 32, color: '#1976D2', mb: 0.5 }} />
              <Typography color="text.secondary" variant="body2">Accessibility Users</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976D2' }}>
                {totalAccessUsers}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <QuestionAnswer sx={{ fontSize: 32, color: '#7B1FA2', mb: 0.5 }} />
              <Typography color="text.secondary" variant="body2">Accessibility Queries</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#7B1FA2' }}>
                {accessibilityQueries.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Description sx={{ fontSize: 32, color: '#00897B', mb: 0.5 }} />
              <Typography color="text.secondary" variant="body2">AI Narratives</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#00897B' }}>
                {narrativeCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <RateReview sx={{ fontSize: 32, color: '#E65100', mb: 0.5 }} />
              <Typography color="text.secondary" variant="body2">Access Reviews</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#E65100' }}>
                {accessReviewCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ImageSearch sx={{ fontSize: 32, color: '#2E7D32', mb: 0.5 }} />
              <Typography color="text.secondary" variant="body2">Alt Text Coverage</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                {altTextStats.total > 0 ? Math.round((altTextStats.withAlt / altTextStats.total) * 100) : 0}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {altTextStats.withAlt}/{altTextStats.total} venues
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 32, color: '#523384', mb: 0.5 }} />
              <Typography color="text.secondary" variant="body2">AI Active Users</Typography>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#523384' }}>
                {uniqueAccessAiUsers}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                using accessibility Q&A
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Privacy notice */}
      <Alert severity="info" sx={{ mb: 3 }}>
        All metrics are aggregated counts — no individual user identities, locations, or session data
        are displayed. Compliant with Piatto Privacy Policy (location data used ephemerally only,
        never persisted or associated with user identity).
      </Alert>

      {/* Charts Row 1: Accessibility queries over time + Feature engagement pie */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Accessibility Queries Over Time
              </Typography>
              {queriesOverTime.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={queriesOverTime}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="queries" stroke="#1976D2" strokeWidth={2} name="Accessibility Queries" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No accessibility queries recorded in this period
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                AI Feature Engagement
              </Typography>
              {featureEngagement.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={featureEngagement}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {featureEngagement.map((entry, idx) => (
                        <Cell key={entry.name} fill={featureColors[idx % featureColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No feature engagement data
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 2: Accessibility user signup trend + State distribution */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Accessibility User Registrations (by Month)
              </Typography>
              {signupTrend.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={signupTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#1976D2" name="New Users" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No registration data
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Accessibility Users by State
              </Typography>
              {stateData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={stateData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={80} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#7B1FA2" name="Users" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
                  No state data
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Query source breakdown */}
      {accessSourceData.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Accessibility Query Source
            </Typography>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={accessSourceData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" name="Queries">
                  {accessSourceData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={entry.name === 'Voice Search' ? '#523384' : '#B91C1C'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Data source usage — how often accessibility data is referenced in AI queries */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Accessibility Data Source Usage in AI Queries
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Number of AI queries that included accessibility data as a source for generating responses.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#E3F2FD', borderRadius: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976D2' }}>
                  {accessDataSourceUsage.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Queries using accessibility data
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#F3E5F5', borderRadius: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#7B1FA2' }}>
                  {aiEvents.length > 0 ? ((accessDataSourceUsage.length / aiEvents.length) * 100).toFixed(1) : 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  of all AI queries
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#E8F5E9', borderRadius: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#2E7D32' }}>
                  {narrativeCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Venues with AI narrative
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FFF3E0', borderRadius: 1 }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#E65100' }}>
                  {accessReviewCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed access reviews
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AccessibilityAnalyticsTab;
